import { useTranslation } from "react-i18next";
import type { FunctionComponent } from "@/common/types/types";
import { Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { SignInSchema, type SignInSchemaType } from "@/schemas/signIn.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuthStore } from "@/store/auth.store";
import { useEffect } from "react";
import TwoFactorForm from "./components/TwoFactorForm";
import useAuth2FA from "@/hooks/user/useAuth2FA";
import { AUTH_ERRORS } from "@/common/errors";
import { SignInForm } from "./components/SignInForm";
import { SignHeader } from "@/components/ui/SignHeader";
import { NewVerifyEmailLinToast } from "./components/NewVerifyEmailLinkToast";

export const Login = (): FunctionComponent => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const router = useRouter();
  const { signIn, loading, error, user } = useAuthStore();
  const {
    requestSMSCode,
    handleEnterCode,
    loading: loadingTwoFactor,
    error: errorTwoFactor,
    showTwoFactorForm,
  } = useAuth2FA();

  const form = useForm<SignInSchemaType>({
    resolver: zodResolver(SignInSchema),
    shouldFocusError: true,
    defaultValues: {
      email: "",
      password: "",
    },
  });

  useEffect(() => {
    if (error == AUTH_ERRORS.MULTI_FACTOR_REQUIRED) {
      void requestSMSCode(form.getValues().email, form.getValues().password);
    }
    if (
      error == AUTH_ERRORS.INVALID_CREDENTIALS ||
      error == AUTH_ERRORS.USER_NOT_FOUND ||
      error == AUTH_ERRORS.WRONG_PASSWORD
    )
      form.setError("root", {
        type: "manual",
        message: t("login.form.messages.invalidCredentials"),
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const onSubmit = async (values: SignInSchemaType): Promise<void> => {
    try {
      await signIn(values.email, values.password);
    } catch {
      // soft catch
    }
  };

  useEffect(() => {
    if (user) {
      const searchParameters = new URLSearchParams(
        router.state.location.search as string
      );
      const redirectUrl = searchParameters.get("redirect") as
        | string
        | undefined;
      if (redirectUrl) {
        window.location.replace(redirectUrl);
      } else {
        void navigate({ to: "/visits" });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  if (showTwoFactorForm) {
    return (
      <TwoFactorForm
        error={errorTwoFactor}
        loading={loadingTwoFactor}
        onSubmit={handleEnterCode}
      />
    );
  }

  return (
    <div className="w-full flex flex-col justify-center items-center">
      <div className="w-full flex flex-col max-w-[450px] px-4 gap-4">
        <SignHeader title={t("login.title")} />
        {error == AUTH_ERRORS.EMAIL_NOT_VERIFIED && <NewVerifyEmailLinToast />}
        <SignInForm form={form} isSubmitting={loading} onSubmit={onSubmit} />
        <Link
          className="text-tinted-grey-4 text-center font-normal text-base"
          to={"/sign-up"}
        >
          {t("login.noAccount")}{" "}
          <span className="text-primary cursor-pointer">
            {t("login.signUpLink")}
          </span>
        </Link>
      </div>
    </div>
  );
};
